import { FC, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';

import BackToInquiriesButton from '@components/BackToInquiriesButton/BackToInquiriesButton';
import CardWithShadow from '@components/CardWithShadow/CardWithShadow';
import EmptyCard from '@components/EmptyCard/EmptyCard';
import Loadable from '@components/Loadable/Loadable';
import useToggle from '@hooks/useToggle';
import { IDietarySupplementTableData } from '@interfaces/supplements';
import urls from '@routes/urls';
import { handleAddSupplementToInquiry } from '@store/features/inquiryDraft/addSupplementToInquiry/actions';
import { handleCreateInquiry } from '@store/features/inquiryDraft/createInquiry/actions';
import { createInquirySelector } from '@store/features/inquiryDraft/createInquiry/selectors';
import { removeCreateInquiry } from '@store/features/inquiryDraft/createInquiry/slice';
import { handleDeleteInquiryDraft } from '@store/features/inquiryDraft/deleteInquiryDraft/actions';
import { deleteInquiryDraftIsLoadingSelector } from '@store/features/inquiryDraft/deleteInquiryDraft/selectors';
import { handleGetInquiryDetails } from '@store/features/inquiryDraft/inquiryDetails/actions';
import { removeInquiryDetails } from '@store/features/inquiryDraft/inquiryDetails/slice';
import { handleRemoveSupplementFromInquiryDraft } from '@store/features/inquiryDraft/removeSupplementFromInquiryDraft/actions';
import { removeSupplementFromInquiryDraftIsLoadingSelector } from '@store/features/inquiryDraft/removeSupplementFromInquiryDraft/selectors';
import { handleGetSupplementDetails } from '@store/features/inquiryDraft/supplementDetails/actions';
import {
  supplementDetailsErrorSelector,
  supplementDetailsIsLoadingSelector,
} from '@store/features/inquiryDraft/supplementDetails/selectors';
import { removeDietarySupplementDetails } from '@store/features/inquiryDraft/supplementDetails/slice';
import { removeDietarySupplementSearch } from '@store/features/inquiryDraft/supplementSearch/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';

import InquirySupplementsCard from './InquirySupplementsCard/InquirySupplementsCard';
import NewSupplementForReviewCTACard from './NewSupplementForReviewCTACard/NewSupplementForReviewCTACard';
import NotificationModal from './NotificationModal/NotificationModal';
import {
  INotificationModalData,
  TSupplementType,
} from './NotificationModal/NotificationModal.interface';
import SubmitInquiryModal from './SubmitInquiryModal/SubmitInquiryModal';
import SubmitInquirySuccessModal from './SubmitInquirySuccessModal/SubmitInquirySuccessModal';
import SupplementSearchResultCard from './SupplementSearchResultCard/SupplementSearchResultCard';
import SupplementSearchSelectBox from './SupplementSearchSelectBox/SupplementSearchSelectBox';
import UserEnteredSupplementModal from './UserEnteredSupplementModal/UserEnteredSupplementModal';

import './style.scss';

const InquiryDraftPage: FC = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { inquiryId } = useParams();
  const inquiry = Number(inquiryId);
  const eventBus = useEventBus('inquiryPage');

  const dietarySupplementDetailsIsLoading = useSelector(supplementDetailsIsLoadingSelector);
  const dietarySupplementDetailsError = useSelector(supplementDetailsErrorSelector);

  const createInquiry = useSelector(createInquirySelector);

  const deleteSupplementFromInquiryDraftIsLoading = useSelector(
    removeSupplementFromInquiryDraftIsLoadingSelector,
  );
  const deleteInquiryDraftIsLoading = useSelector(deleteInquiryDraftIsLoadingSelector);

  const [selectedDietarySupplement, setSelectedDietarySupplement] =
    useState<IDietarySupplementTableData | null>(null);
  const [selectedUserEnteredSupplementId, setSelectedUserEnteredSupplementId] = useState<
    number | null
  >(null);

  const [isAddSupplementModalOpen, onAddSupplementModalOpen, onAddSupplementModalClose] =
    useToggle();
  const [isNotificationModalOpen, onNotificationModalOpen, onNotificationModalClose] = useToggle();

  const [isSubmitInquiryModalOpen, onSubmitInquiryModalOpen, onSubmitInquiryModalClose] =
    useToggle();
  const [isSuccessModalOpen, onSuccessModalOpen, onSuccessModalClose] = useToggle();

  const [dietarySupplementInModal, setDietarySupplementInModal] =
    useState<INotificationModalData | null>(null);

  const clearWorkground = useCallback((): void => {
    setSelectedDietarySupplement(null);
    dispatch(removeInquiryDetails());
    dispatch(removeDietarySupplementSearch());
    dispatch(removeDietarySupplementDetails());
    dispatch(removeCreateInquiry());
  }, [dispatch]);

  useEffect(() => clearWorkground, [clearWorkground]);

  useEffect(() => {
    !inquiry && clearWorkground();
    if (!inquiry || deleteSupplementFromInquiryDraftIsLoading) return;

    dispatch(handleGetInquiryDetails({ id: inquiry }));
  }, [dispatch, inquiry, deleteSupplementFromInquiryDraftIsLoading, clearWorkground]);

  useEffect(() => {
    if (!createInquiry) {
      return;
    }
    if (createInquiry.medicationInquiriesDto?.length) {
      dispatch(removeCreateInquiry());
      return;
    }
    navigate(generatePath(urls.inquiryDetails, { inquiryId: `${createInquiry.id}` }), {
      replace: false,
    });
  }, [navigate, createInquiry]);

  useEffect(() => {
    if (!deleteInquiryDraftIsLoading) return;

    clearWorkground();
    navigate(urls.inquiryDraft, {
      replace: false,
    });
  }, [navigate, dispatch, deleteInquiryDraftIsLoading, clearWorkground]);

  const handleSelectSupplement = useCallback(
    (
      event: SyntheticEvent | null,
      value: IDietarySupplementTableData | null,
      userEnteredSupplementId: number | null = null,
    ): void => {
      setSelectedDietarySupplement(value);
      setSelectedUserEnteredSupplementId(userEnteredSupplementId);

      if (value) {
        if (value.isMedication) return;

        dispatch(handleGetSupplementDetails({ id: value.id }));
      } else {
        dispatch(removeDietarySupplementSearch());
        dispatch(removeDietarySupplementDetails());
      }

      userEnteredSupplementId && onAddSupplementModalOpen();
    },
    [dispatch, onAddSupplementModalOpen],
  );

  const handleAddToInquiry = useCallback(
    (supplementId: number | null = null, userEnteredSupplementId: number | null = null): void => {
      if (!inquiry) {
        dispatch(handleCreateInquiry({ supplementId, userEnteredSupplementId }));
      } else {
        dispatch(
          handleAddSupplementToInquiry({
            inquiryId: inquiry,
            supplementId,
            userEnteredSupplementId,
          }),
        );
      }
    },
    [dispatch, inquiry],
  );

  const handleConfirmRemovalFromInquiry = useCallback(
    (data: INotificationModalData) => {
      if (!data) {
        return;
      }
      const id = Number(data.itemId);
      if (data.type === TSupplementType.INQUIRY) {
        dispatch(handleDeleteInquiryDraft({ id }));
      } else if (data.type === TSupplementType.INQUIRY_ITEM) {
        dispatch(handleRemoveSupplementFromInquiryDraft({ id }));
      }
    },
    [dispatch],
  );

  const handleRetryFetchSelectedSupplementDetails = useCallback(() => {
    selectedDietarySupplement &&
      dispatch(handleGetSupplementDetails({ id: selectedDietarySupplement.id }));
  }, [dispatch, selectedDietarySupplement]);

  // TODO: Needs refactoring
  const handleOpenModalConfirmation = useCallback(
    (supplementId: number, type: number): void => {
      onNotificationModalOpen();
      setDietarySupplementInModal({ itemId: supplementId, type });
    },
    [onNotificationModalOpen],
  );

  useEffect(() => {
    eventBus.on(
      [
        EventType.INQUIRY_SUBMIT_SUCCESS,
        EventType.EDIT_NEW_SUPPLEMENT_SUCCESS,
        EventType.ADD_NEW_SUPPLEMENT_TO_INQUIRY_SUCCESS,
      ],
      () => inquiry && dispatch(handleGetInquiryDetails({ id: inquiry })),
    );

    eventBus.on(EventType.INQUIRY_SUBMIT_SUCCESS, () => {
      onSubmitInquiryModalClose();
      onSuccessModalOpen();
    });

    return () =>
      eventBus.off([
        EventType.INQUIRY_SUBMIT_SUCCESS,
        EventType.EDIT_NEW_SUPPLEMENT_SUCCESS,
        EventType.ADD_NEW_SUPPLEMENT_TO_INQUIRY_SUCCESS,
      ]);
  }, [eventBus, dispatch, inquiry, onSubmitInquiryModalClose, onSuccessModalOpen]);

  return (
    <Container maxWidth="lg" className="page inquiry-draft-page">
      <BackToInquiriesButton />

      <Typography variant="h1" className="inquiry-draft-page__title">
        {t('dietarySupplementInquiry.title')}
      </Typography>

      <Typography variant="body1" className="inquiry-draft-page__description">
        {t('dietarySupplementInquiry.description')}
      </Typography>

      <Box className="inquiry-draft-page__control-part">
        <SupplementSearchSelectBox
          selected={selectedDietarySupplement}
          onSelect={handleSelectSupplement}
        />

        <InquirySupplementsCard
          isSuplementary
          onSelectSupplement={handleSelectSupplement}
          onRemoveSupplementFromInquiry={id =>
            handleOpenModalConfirmation(id, TSupplementType.INQUIRY_ITEM)
          }
          onDeleteInquiry={id => handleOpenModalConfirmation(id, TSupplementType.INQUIRY)}
          onSubmitInquiry={onSubmitInquiryModalOpen}
        />

        <Box className="inquiry-draft-page__right-aside">
          <CardWithShadow className="inquiry-draft-page__search-result">
            <Loadable
              isLoading={dietarySupplementDetailsIsLoading}
              error={dietarySupplementDetailsError && t('errors.supplementError')}
              onRetry={handleRetryFetchSelectedSupplementDetails}
            >
              {selectedDietarySupplement ? (
                <SupplementSearchResultCard
                  dietarySupplementName={selectedDietarySupplement.name}
                  isMedication={selectedDietarySupplement.isMedication}
                  addToInquiry={handleAddToInquiry}
                />
              ) : (
                <EmptyCard
                  icon="/images/img-dietarysupplement.svg"
                  description={t('dietarySupplementInquiry.emptyResultDescription')}
                  borderType="none"
                />
              )}
            </Loadable>
          </CardWithShadow>

          {(!selectedDietarySupplement || selectedDietarySupplement.isMedication) && (
            <Box className="inquiry-draft-page__submit-an-inquiry-cta-card">
              <NewSupplementForReviewCTACard onClick={onAddSupplementModalOpen} />
            </Box>
          )}
        </Box>
      </Box>

      <UserEnteredSupplementModal
        userEnteredSupplementId={selectedUserEnteredSupplementId}
        onAddToInquiry={handleAddToInquiry}
        onClose={onAddSupplementModalClose}
        onExited={() => setSelectedUserEnteredSupplementId(null)}
        open={isAddSupplementModalOpen}
      />

      <NotificationModal
        dataInModal={dietarySupplementInModal}
        onClose={onNotificationModalClose}
        onConfirm={handleConfirmRemovalFromInquiry}
        onExited={() => setDietarySupplementInModal(null)}
        open={isNotificationModalOpen}
      />

      <SubmitInquiryModal
        open={isSubmitInquiryModalOpen}
        submitButtonTitle={t('submitInquiry.submitText')}
        inquiryNumber={inquiry}
        onClose={onSubmitInquiryModalClose}
      />

      <SubmitInquirySuccessModal
        open={isSuccessModalOpen}
        onClose={onSuccessModalClose}
        title={t('submitInquiry.confirmationTitle')}
        onExited={() => navigate(urls.inquiries)}
      />
    </Container>
  );
};

export default InquiryDraftPage;
